/* eslint-disable no-console */

/**
 * Localization Service Module
 * Handles multi-language support for Swiss market (DE, FR, IT, EN)
 */

export class LocalizationService {
  constructor() {
    this.currentLanguage = 'de';
    this.translations = {};
    this.fallbackLanguage = 'en';

    // Swiss language hierarchy priority
    this.languagePriority = ['de', 'fr', 'it', 'en'];
  }

  /**
   * Initialize localization service
   */
  async init(language = null) {
    try {
      console.log('🌍 Initializing Localization Service...');

      // Detect language if not provided
      // Ensure we always work with a string value
      const detectedLanguage = String(language || this.detectBrowserLanguage() || 'de').toLowerCase();
      const validatedLanguage = this.validateLanguage(detectedLanguage);

      // Ensure currentLanguage is always a string
      this.currentLanguage = validatedLanguage ? detectedLanguage : 'de';

      // Load translation files
      await this.loadTranslations();

      // Apply language to UI
      this.applyLanguage();

      console.log(`✅ Localization initialized: ${String(this.currentLanguage).toUpperCase()}`);
      return true;
    } catch (error) {
      console.error('❌ Localization initialization failed:', error);
      // Set safe fallback
      this.currentLanguage = 'de';
      return false;
    }
  }

  /**
   * Load all translation files
   */
  async loadTranslations() {
    try {
      // Load core translations
      const coreTranslations = this.getCoreTranslations();
      this.translations = { ...coreTranslations };

      // Load UI translations
      const uiTranslations = this.getUITranslations();
      this.mergeTranslations(uiTranslations);

      // Load SUVA-specific translations
      const suvaTranslations = this.getSUVATranslations();
      this.mergeTranslations(suvaTranslations);
    } catch (error) {
      console.error('Failed to load translations:', error);
      throw error;
    }
  }

  /**
   * Get core translations for all languages
   */
  getCoreTranslations() {
    return {
      de: {
        app: {
          title: 'ASCII Tree Builder',
          subtitle: 'Arbeitssicherheit & Gefährdungsbeurteilung',
          loading: 'Laden...',
          error: 'Fehler',
          success: 'Erfolg',
          warning: 'Warnung',
          info: 'Information'
        },
        navigation: {
          builder: 'Struktur Builder',
          assessment: 'Sicherheitsanalyse',
          export: 'Export'
        },
        actions: {
          save: 'Speichern',
          cancel: 'Abbrechen',
          delete: 'Löschen',
          edit: 'Bearbeiten',
          add: 'Hinzufügen',
          close: 'Schliessen',
          confirm: 'Bestätigen'
        }
      },
      fr: {
        app: {
          title: 'ASCII Tree Builder',
          subtitle: 'Sécurité au travail & Évaluation des dangers',
          loading: 'Chargement...',
          error: 'Erreur',
          success: 'Succès',
          warning: 'Avertissement',
          info: 'Information'
        },
        navigation: {
          builder: 'Constructeur de structure',
          assessment: 'Analyse de sécurité',
          export: 'Exportation'
        },
        actions: {
          save: 'Enregistrer',
          cancel: 'Annuler',
          delete: 'Supprimer',
          edit: 'Modifier',
          add: 'Ajouter',
          close: 'Fermer',
          confirm: 'Confirmer'
        }
      },
      it: {
        app: {
          title: 'ASCII Tree Builder',
          subtitle: 'Sicurezza sul lavoro & Valutazione dei pericoli',
          loading: 'Caricamento...',
          error: 'Errore',
          success: 'Successo',
          warning: 'Avvertimento',
          info: 'Informazione'
        },
        navigation: {
          builder: 'Costruttore di struttura',
          assessment: 'Analisi della sicurezza',
          export: 'Esportazione'
        },
        actions: {
          save: 'Salvare',
          cancel: 'Annullare',
          delete: 'Eliminare',
          edit: 'Modificare',
          add: 'Aggiungere',
          close: 'Chiudere',
          confirm: 'Confermare'
        }
      },
      en: {
        app: {
          title: 'ASCII Tree Builder',
          subtitle: 'Workplace Safety & Hazard Assessment',
          loading: 'Loading...',
          error: 'Error',
          success: 'Success',
          warning: 'Warning',
          info: 'Information'
        },
        navigation: {
          builder: 'Structure Builder',
          assessment: 'Safety Analysis',
          export: 'Export'
        },
        actions: {
          save: 'Save',
          cancel: 'Cancel',
          delete: 'Delete',
          edit: 'Edit',
          add: 'Add',
          close: 'Close',
          confirm: 'Confirm'
        }
      }
    };
  }

  /**
   * Get UI-specific translations
   */
  getUITranslations() {
    return {
      de: {
        builder: {
          section_title: 'Betriebsstruktur erstellen',
          business_type: 'Betriebsart auswählen...',
          ai_generate: 'AI Generieren',
          add_node: 'Knoten hinzufügen',
          delete_node: 'Löschen',
          expand_all: 'Alle erweitern',
          collapse_all: 'Alle zuklappen',
          ascii_preview: 'ASCII Vorschau',
          copy_ascii: 'Kopieren',
          save_ascii: 'Als Text speichern'
        },
        assessment: {
          section_title: 'SUVA Sicherheitsanalyse',
          start_analysis: 'Analyse starten',
          configuration: 'Konfiguration',
          hazards_per_area: 'Gefahren pro Bereich',
          assessment_profile: 'Profil',
          profile_standard: 'Standard (Innenräume)',
          profile_comprehensive: 'Umfassend (Innen + Außen)'
        },
        export: {
          section_title: 'Bericht exportieren',
          format_excel: 'Excel (.xlsx)',
          format_csv: 'CSV',
          company_name: 'Firmenname',
          assessor_name: 'Bewerter',
          include_remediation: 'Sanierungsempfehlungen einschliessen',
          include_costs: 'Kostenschätzungen einschliessen',
          preview: 'Vorschau'
        }
      },
      fr: {
        builder: {
          section_title: 'Créer la structure de l\'entreprise',
          business_type: 'Sélectionner le type d\'entreprise...',
          ai_generate: 'Génération IA',
          add_node: 'Ajouter un nœud',
          delete_node: 'Supprimer',
          expand_all: 'Développer tout',
          collapse_all: 'Replier tout',
          ascii_preview: 'Aperçu ASCII',
          copy_ascii: 'Copier',
          save_ascii: 'Sauvegarder comme texte'
        },
        assessment: {
          section_title: 'Analyse de sécurité SUVA',
          start_analysis: 'Démarrer l\'analyse',
          configuration: 'Configuration',
          hazards_per_area: 'Dangers par zone',
          assessment_profile: 'Profil',
          profile_standard: 'Standard (Intérieur)',
          profile_comprehensive: 'Complet (Intérieur + Extérieur)'
        },
        export: {
          section_title: 'Exporter le rapport',
          format_excel: 'Excel (.xlsx)',
          format_csv: 'CSV',
          company_name: 'Nom de l\'entreprise',
          assessor_name: 'Évaluateur',
          include_remediation: 'Inclure les recommandations de remédiation',
          include_costs: 'Inclure les estimations de coûts',
          preview: 'Aperçu'
        }
      },
      it: {
        builder: {
          section_title: 'Creare la struttura aziendale',
          business_type: 'Seleziona il tipo di azienda...',
          ai_generate: 'Generazione IA',
          add_node: 'Aggiungi nodo',
          delete_node: 'Elimina',
          expand_all: 'Espandi tutto',
          collapse_all: 'Comprimi tutto',
          ascii_preview: 'Anteprima ASCII',
          copy_ascii: 'Copia',
          save_ascii: 'Salva come testo'
        },
        assessment: {
          section_title: 'Analisi di sicurezza SUVA',
          start_analysis: 'Avvia analisi',
          configuration: 'Configurazione',
          hazards_per_area: 'Pericoli per area',
          assessment_profile: 'Profilo',
          profile_standard: 'Standard (Interno)',
          profile_comprehensive: 'Completo (Interno + Esterno)'
        },
        export: {
          section_title: 'Esporta report',
          format_excel: 'Excel (.xlsx)',
          format_csv: 'CSV',
          company_name: 'Nome azienda',
          assessor_name: 'Valutatore',
          include_remediation: 'Includi raccomandazioni di bonifica',
          include_costs: 'Includi stime dei costi',
          preview: 'Anteprima'
        }
      },
      en: {
        builder: {
          section_title: 'Create Business Structure',
          business_type: 'Select business type...',
          ai_generate: 'AI Generate',
          add_node: 'Add Node',
          delete_node: 'Delete',
          expand_all: 'Expand All',
          collapse_all: 'Collapse All',
          ascii_preview: 'ASCII Preview',
          copy_ascii: 'Copy',
          save_ascii: 'Save as Text'
        },
        assessment: {
          section_title: 'SUVA Safety Analysis',
          start_analysis: 'Start Analysis',
          configuration: 'Configuration',
          hazards_per_area: 'Hazards per area',
          assessment_profile: 'Profile',
          profile_standard: 'Standard (Indoor)',
          profile_comprehensive: 'Comprehensive (Indoor + Outdoor)'
        },
        export: {
          section_title: 'Export Report',
          format_excel: 'Excel (.xlsx)',
          format_csv: 'CSV',
          company_name: 'Company Name',
          assessor_name: 'Assessor',
          include_remediation: 'Include remediations',
          include_costs: 'Include cost estimates',
          preview: 'Preview'
        }
      }
    };
  }

  /**
   * Get SUVA-specific translations
   */
  getSUVATranslations() {
    return {
      de: {
        suva_categories: {
          mechanical: 'Mechanische Gefährdungen',
          falling: 'Sturzgefährdungen',
          electrical: 'Elektrische Gefährdungen',
          hazardous_substances: 'Gesundheitsgefährdende Stoffe (chemisch/biologisch)',
          fire_explosion: 'Brand- und Explosionsgefährdungen',
          thermal: 'Thermische Gefährdungen',
          special_physical: 'Spezielle physikalische Belastungen',
          environmental: 'Belastungen durch Arbeitsumgebungsbedingungen',
          musculoskeletal: 'Belastungen am Bewegungsapparat',
          psychological: 'Psychische Belastungen',
          unexpected_actions: 'Unerwartete Aktionen',
          energy_failure: 'Ausfall der Energieversorgung',
          organizational: 'Arbeitsorganisation'
        },
        risk_levels: {
          high: 'Hoch',
          medium: 'Mittel',
          low: 'Gering'
        },
        priorities: {
          immediate: 'Sofort',
          urgent: 'Fristig',
          medium: 'Mittelfristig',
          low: 'Langfristig'
        },
        stop_levels: {
          S: 'Substitution',
          T: 'Technische Massnahmen',
          O: 'Organisatorische Massnahmen',
          P: 'Persönliche Schutzausrüstung'
        }
      },
      fr: {
        suva_categories: {
          mechanical: 'Dangers mécaniques',
          falling: 'Risques de chute',
          electrical: 'Dangers électriques',
          hazardous_substances: 'Substances dangereuses (chimique/biologique)',
          fire_explosion: 'Risques d’incendie et d’explosion',
          thermal: 'Risques thermiques',
          special_physical: 'Contraintes physiques particulières',
          environmental: 'Contraintes liées à l’environnement de travail',
          musculoskeletal: 'Contraintes musculo-squelettiques',
          psychological: 'Contraintes psychiques',
          unexpected_actions: 'Actions inattendues',
          energy_failure: 'Défaillances de l’alimentation énergétique',
          organizational: 'Organisation du travail'
        },
        risk_levels: {
          high: 'Élevé',
          medium: 'Moyen',
          low: 'Faible'
        },
        priorities: {
          immediate: 'Immédiat',
          urgent: 'Urgent',
          medium: 'Moyen terme',
          low: 'Long terme'
        },
        stop_levels: {
          S: 'Substitution',
          T: 'Mesures techniques',
          O: 'Mesures organisationnelles',
          P: 'Équipement de protection individuelle'
        }
      },
      it: {
        suva_categories: {
          mechanical: 'Pericoli meccanici',
          falling: 'Pericoli di caduta',
          electrical: 'Pericoli elettrici',
          hazardous_substances: 'Sostanze pericolose (chimiche/biologiche)',
          fire_explosion: 'Pericoli di incendio ed esplosione',
          thermal: 'Pericoli termici',
          special_physical: 'Esposizioni fisiche speciali',
          environmental: 'Fattori ambientali di lavoro',
          musculoskeletal: 'Sollecitazioni dell’apparato motorio',
          psychological: 'Carichi psicologici',
          unexpected_actions: 'Azioni inattese',
          energy_failure: 'Guasti all’alimentazione energetica',
          organizational: 'Organizzazione del lavoro'
        },
        risk_levels: {
          high: 'Alto',
          medium: 'Medio',
          low: 'Basso'
        },
        priorities: {
          immediate: 'Immediato',
          urgent: 'Urgente',
          medium: 'Medio termine',
          low: 'Lungo termine'
        },
        stop_levels: {
          S: 'Sostituzione',
          T: 'Misure tecniche',
          O: 'Misure organizzative',
          P: 'Dispositivi di protezione individuale'
        }
      },
      en: {
        suva_categories: {
          mechanical: 'Mechanical Hazards',
          falling: 'Fall Hazards',
          electrical: 'Electrical Hazards',
          hazardous_substances: 'Hazardous Substances (Chemical/Biological)',
          fire_explosion: 'Fire and Explosion Hazards',
          thermal: 'Thermal Hazards',
          special_physical: 'Special Physical Exposures',
          environmental: 'Work Environment Conditions',
          musculoskeletal: 'Musculoskeletal Strain',
          psychological: 'Psychological Stressors',
          unexpected_actions: 'Unexpected Actions',
          energy_failure: 'Power Supply Failures',
          organizational: 'Organizational Factors'
        },
        risk_levels: {
          high: 'High',
          medium: 'Medium',
          low: 'Low'
        },
        priorities: {
          immediate: 'Immediate',
          urgent: 'Urgent',
          medium: 'Medium-term',
          low: 'Long-term'
        },
        stop_levels: {
          S: 'Substitution',
          T: 'Technical Measures',
          O: 'Organizational Measures',
          P: 'Personal Protective Equipment'
        }
      }
    };
  }

  /**
   * Get translation for a key
   */
  t(key, params = {}) {
    const translation = this.getTranslation(key, this.currentLanguage);
    if (!translation) {
      // Try fallback language
      const fallbackTranslation = this.getTranslation(key, this.fallbackLanguage);
      return this.interpolate(fallbackTranslation || key, params);
    }

    return this.interpolate(translation, params);
  }

  /**
   * Get nested translation value
   */
  getTranslation(key, language) {
    const keys = key.split('.');
    let value = this.translations[language] || {};

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return null;
      }
    }

    return value;
  }

  /**
   * Interpolate parameters into translation
   */
  interpolate(text, params) {
    if (typeof text !== 'string') return text;

    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return params[key] !== undefined ? params[key] : match;
    });
  }

  /**
   * Set application language
   */
  setLanguage(language) {
    if (this.validateLanguage(language)) {
      this.currentLanguage = language;
      this.applyLanguage();
      return true;
    }
    return false;
  }

  /**
   * Get current language
   */
  getLanguage() {
    return this.currentLanguage;
  }

  /**
   * Get supported languages
   */
  getSupportedLanguages() {
    return this.languagePriority.map(code => ({
      code,
      name: this.getLanguageName(code),
      native: this.getNativeLanguageName(code)
    }));
  }

  /**
   * Get language name in English
   */
  getLanguageName(code) {
    const names = {
      de: 'German',
      fr: 'French',
      it: 'Italian',
      en: 'English'
    };
    return names[code] || code;
  }

  /**
   * Get native language name
   */
  getNativeLanguageName(code) {
    const names = {
      de: 'Deutsch',
      fr: 'Français',
      it: 'Italiano',
      en: 'English'
    };
    return names[code] || code;
  }

  /**
   * Validate language code
   */
  validateLanguage(language) {
    if (typeof language !== 'string' || !language) {
      return false;
    }
    return this.languagePriority.includes(language.toLowerCase());
  }

  /**
   * Detect browser language
   */
  detectBrowserLanguage() {
    if (typeof navigator !== 'undefined') {
      const browserLang = navigator.language.split('-')[0].toLowerCase();

      // Check if browser language is supported
      return this.validateLanguage(browserLang) ? browserLang : 'de';
    }

    return 'de'; // Default to German for Swiss market
  }

  /**
   * Apply language to UI elements
   */
  applyLanguage() {
    // Update HTML language attribute
    if (typeof document !== 'undefined') {
      document.documentElement.lang = this.currentLanguage;

      // Update text content for elements with data-i18n attribute
      document.querySelectorAll('[data-i18n]').forEach(element => {
        const key = element.getAttribute('data-i18n');
        element.textContent = this.t(key);
      });

      // Dispatch custom event for language change
      document.dispatchEvent(new CustomEvent('languageChanged', {
        detail: {
          language: this.currentLanguage,
          translations: this.translations[this.currentLanguage]
        }
      }));
    }
  }

  /**
   * Merge translations
   */
  mergeTranslations(newTranslations) {
    for (const [lang, translations] of Object.entries(newTranslations)) {
      if (!this.translations[lang]) {
        this.translations[lang] = {};
      }
      this.deepMerge(this.translations[lang], translations);
    }
  }

  /**
   * Deep merge objects
   */
  deepMerge(target, source) {
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        if (!target[key] || typeof target[key] !== 'object') {
          target[key] = {};
        }
        this.deepMerge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }

  /**
   * Get date format for current language
   */
  getDateFormat() {
    const formats = {
      de: 'dd.MM.yyyy',
      fr: 'dd/MM/yyyy',
      it: 'dd/MM/yyyy',
      en: 'MM/dd/yyyy'
    };
    return formats[this.currentLanguage] || formats.de;
  }

  /**
   * Get current date formatted for current language
   */
  getCurrentDate() {
    const date = new Date();
    const format = this.getDateFormat();

    // Simple date formatting (consider using date-fns in production)
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    switch (format) {
      case 'dd.MM.yyyy':
        return `${day}.${month}.${year}`;
      case 'dd/MM/yyyy':
      case 'MM/dd/yyyy':
        return format === 'MM/dd/yyyy' ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
      default:
        return `${day}.${month}.${year}`;
    }
  }

  /**
   * Get currency format for Swiss market
   */
  getCurrencyFormat(amount) {
    return new Intl.NumberFormat(`${this.currentLanguage}-CH`, {
      style: 'currency',
      currency: 'CHF'
    }).format(amount);
  }
}
